/**
 * Attach the HLS playlist to the video element. Safari plays HLS
 * natively; everywhere else hls.js drives Media Source Extensions and
 * is loaded on demand so the initial payload stays small. The element
 * stays muted permanently: audio always comes from the track files
 * through the engine, never from the video container.
 */
/**
 * Whether this engine plays HLS itself rather than through hls.js.
 * The local media cache asks the same question, so the answer is
 * derived in one place and cannot drift between the two.
 */
export function usesNativeHls(): boolean {
  return document.createElement('video').canPlayType('application/vnd.apple.mpegurl') !== '';
}

export async function attachVideo(
  video: HTMLVideoElement,
  playlistUrl: string,
): Promise<() => void> {
  video.muted = true;
  video.defaultMuted = true;

  if (usesNativeHls()) {
    video.src = playlistUrl;
    return () => {
      video.removeAttribute('src');
      video.load();
    };
  }

  const { default: Hls } = await import('hls.js');
  if (!Hls.isSupported()) {
    throw new Error('This browser supports neither native HLS nor Media Source Extensions');
  }
  const hls = new Hls({
    // VOD review material: favour stability over aggressive buffering.
    maxBufferLength: 30,
    backBufferLength: 30,
  });
  hls.loadSource(playlistUrl);
  hls.attachMedia(video);
  return () => hls.destroy();
}
